import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import GraphViewer from "../../components/GraphPlotter/GraphViewer";
import { CIRCLE_GRAPH_COLORS } from "../../components/GraphPlotter/circleGraphRanges";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

const graphBoxStyle = { margin: "20px auto" };
const GRAPH_CELL_PX = 30;
const GRAPH_MARGINS_PX = 60;

const pointStyle = (x, y, label) => ({
  x,
  y,
  label,
  color: CIRCLE_GRAPH_COLORS.point,
  textColor: CIRCLE_GRAPH_COLORS.point,
});

function CircleDiagram({ circles, points, segments, xRange, yRange }) {
  const span = Math.max(xRange[1] - xRange[0], yRange[1] - yRange[0]);
  const graphWidth = Math.max(350, span * GRAPH_CELL_PX + GRAPH_MARGINS_PX);

  return (
    <div style={{ ...graphBoxStyle, maxWidth: graphWidth }}>
      <GraphViewer
        circles={circles}
        points={points}
        segments={segments}
        xRange={xRange}
        yRange={yRange}
        width={graphWidth}
      />
    </div>
  );
}

const examples = [
  {
    label: "Przykład 1",
    summary: "Odczytywanie środka i promienia z równania",
    description: (
      <p>
        Odczytaj środek i promień okręgu: <InlineMath math={String.raw`(x+3)^2+(y-2)^2=16`} />.
      </p>
    ),
    content: (
      <>
        <p>Znak w nawiasie jest przeciwny do współrzędnej środka.</p>
        <BlockMath math={String.raw`S=(-3,2),\qquad r=\sqrt{16}=4`} />
        <CircleDiagram
          circles={[{ center: { x: -3, y: 2 }, radius: 4, color: CIRCLE_GRAPH_COLORS.circle, width: 3 }]}
          points={{ S: pointStyle(-3, 2, "S") }}
          xRange={[-8, 2]}
          yRange={[-3, 7]}
        />
      </>
    ),
  },
  {
    label: "Przykład 2",
    summary: "Zapisanie równania ze środka i promienia",
    description: (
      <p>
        Napisz równanie okręgu o środku <InlineMath math={String.raw`S=(2,-1)`} />
        {" "}i promieniu <InlineMath math={String.raw`r=5`} />.
      </p>
    ),
    content: (
      <>
        <BlockMath math={String.raw`(x-2)^2+(y-(-1))^2=5^2`} />
        <BlockMath math={String.raw`(x-2)^2+(y+1)^2=25`} />
        <CircleDiagram
          circles={[{ center: { x: 2, y: -1 }, radius: 5, color: CIRCLE_GRAPH_COLORS.circle, width: 3 }]}
          points={{ S: pointStyle(2, -1, "S") }}
          xRange={[-4.5, 8.5]}
          yRange={[-7.5, 5.5]}
        />
      </>
    ),
  },
  {
    label: "Przykład 3",
    summary: "Symetria okręgu względem osi",
    description: (
      <p>
        Okrąg <InlineMath math={String.raw`(x-2)^2+(y+1)^2=9`} /> odbijamy względem osi
        {" "}<InlineMath math={String.raw`OY`} />. Zapisz równanie obrazu.
      </p>
    ),
    content: (
      <>
        <p>W symetrii względem osi <InlineMath math={String.raw`OY`} /> zmienia znak tylko pierwsza współrzędna środka, a promień pozostaje taki sam.</p>
        <BlockMath math={String.raw`S=(2,-1)\longmapsto S'=(-2,-1),\qquad r=3`} />
        <BlockMath math={String.raw`(x+2)^2+(y+1)^2=9`} />
        <CircleDiagram
          circles={[
            { center: { x: 2, y: -1 }, radius: 3, color: CIRCLE_GRAPH_COLORS.circle, width: 3 },
            { center: { x: -2, y: -1 }, radius: 3, color: CIRCLE_GRAPH_COLORS.circle, width: 3 },
          ]}
          points={{ S: pointStyle(2, -1, "S"), "S'": pointStyle(-2, -1, "S'") }}
          xRange={[-6.5, 6.5]}
          yRange={[-5.5, 3.5]}
        />
      </>
    ),
  },
];

export default function CircleEquationPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        Okrąg jest zbiorem punktów oddalonych o tę samą odległość od środka. Jeżeli
        {" "}<InlineMath math={String.raw`S=(a,b)`} /> jest środkiem, a
        {" "}<InlineMath math={String.raw`r`} /> promieniem, to równanie okręgu ma postać:
      </p>
      <BlockMath math={String.raw`(x-a)^2+(y-b)^2=r^2`} />
      <CircleDiagram
        circles={[{ center: { x: 2, y: -1 }, radius: 3, color: CIRCLE_GRAPH_COLORS.circle, width: 3 }]}
        points={{ S: pointStyle(2, -1, "S") }}
        xRange={[-2.5, 6.5]}
        yRange={[-5.5, 3.5]}
      />
      <p>
        Gdy znamy punkt <InlineMath math={String.raw`P`} /> leżący na okręgu, promień liczymy jako
        {" "}<InlineMath math={String.raw`r=|SP|`} />. Gdy podana jest średnica, jej środek jest środkiem
        odcinka, a promień jest równy połowie jej długości.
      </p>
      <p>
        Przy symetrii okręgu odbijamy jego środek, natomiast promień się nie zmienia.
        Dla osi <InlineMath math={String.raw`OX`} /> mamy
        {" "}<InlineMath math={String.raw`(a,b)\mapsto(a,-b)`} />, a dla osi
        {" "}<InlineMath math={String.raw`OY`} />: <InlineMath math={String.raw`(a,b)\mapsto(-a,b)`} />.
      </p>
    </div>
  );

  return (
    <TheoryExamplesRunner
      title="Równanie okręgu"
      intro={intro}
      examples={examples}
    />
  );
}
